import ExponentTool from "../MathTools/ExponentTool";
import SystemSolverTool from "../MathTools/SystemSolverTool";
import QuadraticGraphTool from "../MathTools/QuadraticGraphTool";
import CircleTool from "../MathTools/CircleTool";
import DataAnalyzer from "../MathTools/DataAnalyzer";


export const chapterPages = {
    1: [
      {
        title: <h1>Exponents & Radicals — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Understanding Exponents.</strong> An exponent is a way to represent
              repeated multiplication. For example, instead of writing 2 × 2 × 2 × 2, we write
              2<sup>4</sup> (read as “2 to the power of 4”). The base (2) is the number being
              multiplied, and the exponent (4) tells how many times to multiply the base by itself.
              Exponents grow quickly: 2<sup>4</sup> = 16, 2<sup>5</sup> = 32, 2<sup>6</sup> = 64,
              and so on. This shorthand makes it easier to work with very large or very small numbers
              in science and everyday math.
            </p>
          </>
        )
      },
      {
        title: <h1>Exponents & Radicals — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Exponent Laws for Multiplication and Division.</strong> We can simplify
              expressions using the laws of exponents. When we multiply powers with the same base,
              we add their exponents. When we divide powers with the same base, we subtract their
              exponents. For example:
            </p>
            <pre style={{background:"#f5f5f5", padding:8}}>
              3² × 3⁴ = 3^(2+4) = 3⁶ = 729.
              5⁷ ÷ 5³ = 5^(7−3) = 5⁴ = 625.
            </pre>
            <p>
              These rules work because exponents are repeated multiplication. The laws help to
              simplify complex expressions without expanding them fully.
            </p>
          </>
        )
      },
      {
        title: <h1>Exponents & Radicals — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Zero and Negative Exponents.</strong> Any non-zero number raised to the power
              of 0 is defined to be 1. For instance, 7<sup>0</sup> = 1 and 10<sup>0</sup> = 1. A
              negative exponent means we take the reciprocal of the base. For example, 
              2<sup>−3</sup> = 1/(2<sup>3</sup>) = 1/8. The pattern of exponents becomes clearer if
              we list powers of 2. Zero and negative exponents help extend the idea of powers to
              reciprocals and small fractions.
            </p>
          </>
        )
      },
      {
        title: <h1>Exponents & Radicals — Slide 4</h1>,
        body: (
          <>
            <p>
              <strong>Understanding Radicals (Roots).</strong> A radical is the opposite of using an
              exponent. The square root symbol √ asks “what number squared gives this value?” For
              example, √36 = 6 because 6² = 36. Similarly, ³√8 = 2 is a cube root because 2³ = 8.
              Radicals are useful for finding side lengths from areas (like the side of a square
              with area 36 is 6) or solving equations involving exponents.
            </p>
          </>
        )
      },
      {
        title: <h1>Exponents & Radicals — Slide 5</h1>,
        body: (
          <>
            <p>
              <strong>Exponents and Radicals Connection.</strong> A power with a fractional exponent
              is equivalent to a radical. For example, 9<sup>1/2</sup> = √9 = 3. More generally,
              a<sup>1/2</sup> = √a and a<sup>1/3</sup> = ³√a. This allows rewriting radicals as
              exponents and vice versa. 
            </p>
            <p>
              <em>Try the ExponentTool below</em> to practice entering expressions
              and see them simplified.
            </p>
            <ExponentTool />
          </>
        )
      }
    ],
  
    2: [
      {
        title: <h1>Systems of Equations — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>What is a System of Equations?</strong> A system is a set of two or more
              equations with the same variables, considered together. In Grade 9, we usually deal
              with two linear equations in x and y. A solution is the pair (x,y) that satisfies both
              equations. Graphically, it's the intersection point of the lines (one solution), or 
              they might be parallel (no solution), or identical lines (infinitely many solutions).
            </p>
          </>
        )
      },
      {
        title: <h1>Systems of Equations — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Solving by Graphing.</strong> One way is to plot each line on the same
              coordinate plane and see where they intersect. You can use a graphing tool to do this
              quickly. Graphing provides a visual understanding but can be imprecise if the
              intersection is not on nice integer coordinates.
            </p>
          </>
        )
      },
      {
        title: <h1>Systems of Equations — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Solving by Substitution.</strong> Solve one equation for one variable and then
              substitute it into the other. For example:
            </p>
            <pre style={{background:"#f5f5f5", padding:8}}>
  {`y = 3x + 1
  4x + y = 17
  
  Substitute y => 4x + (3x+1) = 17 => 7x+1=17 => x=16/7 => y=55/7`}
            </pre>
            <p>
              Substitution is often handy if one equation is already solved for y or x.
            </p>
          </>
        )
      },
      {
        title: <h1>Systems of Equations — Slide 4</h1>,
        body: (
          <>
            <p>
              <strong>Solving by Elimination.</strong> You add or subtract equations to eliminate
              one variable. For instance:
            </p>
            <pre style={{background:"#f5f5f5", padding:8}}>
  {`2x + 3y = 13
  -2x +  y = 1
  -----------
        4y = 14 => y=3.5 => plug back to find x`}
            </pre>
            <p>
              If the coefficients don’t line up nicely, you may multiply one or both equations first.
            </p>
          </>
        )
      },
      {
        title: <h1>Systems of Equations — Slide 5</h1>,
        body: (
          <>
            <p>
              <strong>Special Cases and Tips.</strong> Parallel lines =&gt no solution. Identical
              lines =&gt infinitely many solutions. Always check your solution by plugging back
              into both original equations. 
            </p>
            <p>
              <em>Use the SystemSolverTool below</em> to enter two linear equations and find their
              intersection by graphing/substitution/elimination automatically.
            </p>
            <SystemSolverTool />
          </>
        )
      }
    ],
  
    3: [
      {
        title: <h1>Quadratic Functions — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Introduction to Quadratics.</strong> A quadratic function is a polynomial of
              degree 2, typically written y = ax² + bx + c. Its graph is a parabola — a U-shaped (or
              inverted U) curve. Examples: y = x² or y = 2x² − 3x + 5.
            </p>
          </>
        )
      },
      {
        title: <h1>Quadratic Functions — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Parabola Shape and Key Features.</strong> The <em>vertex</em> is the max or
              min point. The <em>axis of symmetry</em> is a vertical line through the vertex. The
              coefficient a determines how wide or narrow the parabola is, and whether it opens up
              (a &gt 0) or down (a &lt; 0).
            </p>
          </>
        )
      },
      {
        title: <h1>Quadratic Functions — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Standard Form and Intercepts.</strong> y = ax² + bx + c. The x-intercepts
              (or roots) occur where ax² + bx + c = 0. Some quadratics have 0, 1, or 2 real roots.
              The y-intercept is simply c (the value of y when x=0).
            </p>
          </>
        )
      },
      {
        title: <h1>Quadratic Functions — Slide 4</h1>,
        body: (
          <>
            <p>
              <strong>Using the QuadraticGraphTool.</strong> Enter a quadratic equation and see
              its graph. Adjust the coefficients a, b, and c to observe how the shape shifts and
              the vertex changes location. 
            </p>
          </>
        )
      },
      {
        title: <h1>Quadratic Functions — Slide 5</h1>,
        body: (
          <>
            <p>
              <strong>Quadratics in Action (Real-Life Example).</strong> The path of a thrown ball
              can be modeled by h(t) = −5t² + 20t + 1. The parabola opens downward, so it has a
              maximum height at the vertex. 
            </p>
            <p>
              <em>Try the QuadraticGraphTool below:</em>
            </p>
            <QuadraticGraphTool />
          </>
        )
      }
    ],
  
    4: [
      {
        title: <h1>Geometry of Circles — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Circle Basics – Radius and Diameter.</strong> A circle is a round shape where
              every point along its edge is the same distance from the center. That distance is
              called the radius (r). The diameter (d) is 2r. The boundary is called the circumference.
            </p>
          </>
        )
      },
      {
        title: <h1>Geometry of Circles — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Circumference – The Perimeter of a Circle.</strong> The circumference C of a
              circle is given by C = 2πr (or πd). π (pi) is about 3.14. 
            </p>
          </>
        )
      },
      {
        title: <h1>Geometry of Circles — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Area of a Circle.</strong> The area A of a circle is given by A = πr². 
              If r=5, the area is 25π (≈78.5).
            </p>
          </>
        )
      },
      {
        title: <h1>Geometry of Circles — Slide 4</h1>,
        body: (
          <>
            <p>
              <strong>Exploring Circle Measurements with Tools.</strong> Use the CircleTool to see
              how changing the radius affects the circumference and area. Doubling the radius
              doubles the circumference but <em>quadruples</em> the area.
            </p>
          </>
        )
      },
      {
        title: <h1>Geometry of Circles — Slide 5</h1>,
        body: (
          <>
            <p>
              <strong>Applying Circle Formulas.</strong> For a circle of diameter 10, the radius
              is 5. Then C=2π(5)=31.4 (approx), and A=25π=78.5 (approx). 
            </p>
            <p>
              <em>Try the CircleTool below:</em>
            </p>
            <CircleTool />
          </>
        )
      }
    ],
  
    5: [
      {
        title: <h1>Statistics & Probability — Slide 1</h1>,
        body: (
          <>
            <p>
              <strong>Describing Data – Mean, Median, Mode.</strong> We often summarize data with
              these three measures of center. The mean is the average, the median is the middle
              value in sorted data, and the mode is the most frequent value.
            </p>
          </>
        )
      },
      {
        title: <h1>Statistics & Probability — Slide 2</h1>,
        body: (
          <>
            <p>
              <strong>Range and Spread.</strong> The range is the difference between the highest
              and lowest values. Large range =&gt data is more spread out. Outliers can affect the
              mean significantly but not the median as much.
            </p>
          </>
        )
      },
      {
        title: <h1>Statistics & Probability — Slide 3</h1>,
        body: (
          <>
            <p>
              <strong>Visualizing Data – Graphs and Distributions.</strong> Histograms and bar
              graphs help show how data is distributed (whether it’s skewed or symmetric). This
              helps decide whether the mean or median is a more representative measure of center.
            </p>
          </>
        )
      },
      {
        title: <h1>Statistics & Probability — Slide 4</h1>,
        body: (
          <>
            <p>
              <strong>Introduction to Probability.</strong> Probability measures the likelihood of
              events, from 0 (impossible) to 1 (certain). With a fair coin, P(Heads)=1/2. With a
              fair six-sided die, P(rolling a 3)=1/6.
            </p>
          </>
        )
      },
      {
        title: <h1>Statistics & Probability — Slide 5</h1>,
        body: (
          <>
            <p>
              <strong>Compound Events and Independent Events.</strong> For independent events, 
              multiply probabilities for “AND.” For mutually exclusive events, add probabilities
              for “OR.” 
            </p>
            <p>
              <em>Try the DataAnalyzer below</em> to create or analyze data, or run quick
              probability experiments (like coin flips or dice rolls) to see how actual frequencies
              compare to theoretical probabilities.
            </p>
            <DataAnalyzer />
          </>
        )
      }
    ],
  
    6: [
      {
        title: <h1>Grade 9 Summary</h1>,
        body: (
          <>
            <p>
              You’ve covered Exponents & Radicals, Systems of Equations, Quadratic Functions,
              Geometry of Circles, and Statistics & Probability. Well done!
            </p>
            <p>
              You can now try a final quiz of 50 questions across all chapters. Good luck!
            </p>
          </>
        )
      }
    ]
  };